<template>
  <div v-if="zone" class="modal-content-frame">
    <div class="zone-header zone-header__inline">
      <label for="name">Zone Name *</label>
      <div>
        <InputText
          id="name"
          v-model="zone.name"
          placeholder="What's the zone name?"
          :error="!hasUniqueName ? 'Each zone must have a unique name' : ''"
        />
      </div>
    </div>

    <div class="zone-header">
      <div>
        <label for="chips">Zone Type *</label>
        <p>Select one or more types</p>
      </div>
    </div>

    <div class="chips">
      <Tooltip
        v-for="cat in eventsStore.eventList?.zone_category"
        :key="cat.id"
        :hasTooltip="getZoneTooltip(cat.name) !== ''"
      >
        <template v-slot:text>
          <Chip
            :key="cat.id"
            :isToggle="true"
            :colored="zone.categories.includes(cat.id)"
            :canDelete="zone.categories.includes(cat.id)"
            @chipClicked="chipClicked(cat.id)"
          >
            {{ cat.name }}
          </Chip>
        </template>
        <template v-slot:tooltip>
          {{ getZoneTooltip(cat.name) }}
        </template>
      </Tooltip>
    </div>

    <div class="zone-header">
      <div>
        <label for="chips">Sensors *</label>
        <p>List of sensors in this zone</p>
      </div>
      <Button :disabled="loading" severity="primary" @click="goToStageTwo()">
        <IconPlus />
        Add
      </Button>
    </div>

    <ul class="zone-sensor-list">
      <ZoneSensor
        v-for="(sensor, index) in zone.sensors"
        :key="index"
        @removeSensor="removeSensor(index)"
        :sensor="sensor"
        :sensorDistanceOptions="sensorDistanceOptions"
      />
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import type { SensorDistanceSettingType } from '@/types/Sensor';
import type { EventTypeNew, ZoneType } from '@/types/Event';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { getSensorRangeOptions } from '@/utils/api';

import InputText from '@/components/inputs/InputText.vue';
import { Button } from 'primevue';

import Chip from '@/components/global/Chip.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import ZoneSensor from '@/components/sensors/ZoneSensor.vue';

import IconPlus from '@/icons/IconPlus.svg?component';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

import { getZoneTooltip } from '@/utils/zoneTooltips';

const newEvent = defineModel<EventTypeNew>('newEvent');

const zone = defineModel<ZoneType>('zone');

const emit = defineEmits(['changeStage']);

const props = withDefaults(
  defineProps<{
    // disabled?: boolean;
    // defaultSensors?: SensorType[];
    index: number;
  }>(),
  {
    // disabled: false,
  },
);

const sensorDistanceOptions = ref<SensorDistanceSettingType[]>([]);

const chipClicked = (chipId: number) => {
  if (zone.value) {
    if (zone.value.categories.includes(chipId)) {
      zone.value.categories.splice(zone.value.categories.indexOf(chipId), 1);
    } else {
      zone.value.categories.push(chipId);
    }
  }
};

const loading = ref(false);

const hasUniqueName = computed(() => {
  let nameUnique = true;

  // Trick it into ignoring itself when making the comparison
  if (newEvent.value?.zones) {
    const zones = [...newEvent.value.zones];
    zones.splice(props.index, 1);
    zones.forEach((savedZone) => {
      if (savedZone.name.toLowerCase().trim() === zone.value?.name.toLowerCase().trim()) {
        nameUnique = false;
      }
    });
  }

  return nameUnique;
});

const removeSensor = (index: number) => {
  zone.value?.sensors.splice(index, 1);
};

const goToStageTwo = () => {
  emit('changeStage');
};

watch(() => zone.value, (newVal) => {
  if (newVal && newVal.sensors && newVal.sensors.length > 0 && newVal.sensors[0].id) {
    getSensorRangeOptions(userStore.accessToken, userStore.isStaff, newVal.sensors[0].id).then((resp) => {
      sensorDistanceOptions.value = resp;
    });
  }
}, { immediate: true, deep: true });
</script>
